import React, {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useReducer,
  useState,
} from 'react';

// Hooks
import { SaveStatus, useAutosave } from '../hooks';

// Services
import { createLocalProductCardsRepository } from '../services';

// State
import { CardAction, cardActions, normalizeCard, productCardReducer } from '../state';

// Types
import { ProductCard, ProductCardsRepository } from '../types';

interface CardEditorContextValue {
  card: ProductCard | null;
  isLoading: boolean;
  /** The route's `:cardId` matched nothing in the store. */
  notFound: boolean;
  /** Copy to surface when the last autosave failed; null while all is well. */
  saveError: string | null;
  /**
   * The input the server refused, when it named one — today only `"name"`, from
   * the duplicate-card-name rule. Non-null means a retry cannot help, so the
   * indicator withholds its Retry button and the user edits the field instead.
   */
  saveErrorField: string | null;
  /**
   * What the editor tells the user about their work. With no Save control in
   * the module (R8), this is the only evidence they have that it is safe.
   */
  status: SaveStatus;
  /** ISO-8601 time of the last successful write; null until there is one. */
  lastSavedAt: string | null;
  /** True while an edit is debounced, in flight, or waiting on a retry. */
  hasPendingChanges: boolean;
  dispatch: (action: CardAction) => void;
  /** Writes any pending change immediately (used before navigating away). */
  flush: () => void;
  /** Re-attempts the write the user was told about. */
  retry: () => void;
}

const CardEditorContext = createContext<CardEditorContextValue | null>(null);

/**
 * The reducer proper only ever sees a real document; the nullable wrapper here
 * covers the window before the card has loaded.
 */
const editorReducer = (
  state: ProductCard | null,
  action: CardAction
): ProductCard | null => {
  if (action.type === 'loadCard') {
    return normalizeCard(action.card);
  }
  if (!state) {
    return state;
  }
  return productCardReducer(state, action);
};

interface CardEditorProviderProps {
  cardId: string;
  /** Defaults to the browser-storage adapter (ADR-001). */
  repository?: ProductCardsRepository;
  children: React.ReactNode;
}

export const CardEditorProvider = ({
  cardId,
  repository,
  children,
}: CardEditorProviderProps) => {
  const defaultRepository = useMemo(() => createLocalProductCardsRepository(), []);
  const activeRepository = repository ?? defaultRepository;

  const [card, dispatch] = useReducer(editorReducer, null);
  const [isLoading, setIsLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setIsLoading(true);
    setNotFound(false);
    activeRepository
      .get(cardId)
      .then((loaded) => {
        if (cancelled) {
          return;
        }
        if (loaded) {
          dispatch(cardActions.loadCard(loaded));
        } else {
          setNotFound(true);
        }
      })
      .catch(() => {
        if (!cancelled) {
          setNotFound(true);
        }
      })
      .finally(() => {
        if (!cancelled) {
          setIsLoading(false);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [activeRepository, cardId]);

  const { saveError, saveErrorField, status, lastSavedAt, hasPendingChanges, flush, retry } =
    useAutosave(card, activeRepository, !isLoading && !notFound);

  const value = useMemo<CardEditorContextValue>(
    () => ({
      card,
      isLoading,
      notFound,
      saveError,
      saveErrorField,
      status,
      lastSavedAt,
      hasPendingChanges,
      dispatch,
      flush,
      retry,
    }),
    [
      card,
      isLoading,
      notFound,
      saveError,
      saveErrorField,
      status,
      lastSavedAt,
      hasPendingChanges,
      flush,
      retry,
    ]
  );

  return <CardEditorContext.Provider value={value}>{children}</CardEditorContext.Provider>;
};

export const useCardEditor = (): CardEditorContextValue => {
  const context = useContext(CardEditorContext);
  if (!context) {
    throw new Error('useCardEditor must be used inside a CardEditorProvider');
  }
  return context;
};
